import { IconButton, Spinner, type PlaybackTransport } from "@carbon6/c6-frontend-design";
import { useEffect, useMemo, useState } from "react";
import { HatTextTooltip } from "@/components/tooltip";
import { EmptyState } from "@/shared/ui/emptyState/public";
import { inlineSpinnerClassName } from "@/shared/ui/loadingState/public";
import { channelTitle, renderGroup, type ChannelStructureIndex } from "../parquetChannels";
import { gripperSegments } from "./gripperSegments";
import type { MotionWindow } from "./motionSeries";
import { motionSeriesKey, type MotionSeriesSource } from "./motionRendering";
import { MOTION_SERIES_STYLE } from "./motionChannelCards";
import { MotionPlot, type MotionPlotLine } from "./MotionPlot";
import { GripperSettingsDialog, type GripperSettings } from "./GripperSettingsDialog";
import styles from "./GripperSegmentRows.module.css";

type Props = {
  /** 먼저 개요를 담고 (채널, 지표)마다 상세로 바뀌는 색인. 카드 격자와 같은 색인을 쓴다. */
  source: MotionSeriesSource;
  channels: ChannelStructureIndex;
  /**
   * 이 표면이 화면에 보이는지 여부. 다른 지표 탭에서는 `false` 를 전달해 캔버스를 마운트하지
   * 않는다. 그러나 행은 남긴다. 그래서 임계값 설정이 탭 전환 뒤에도 유지된다. 행이 사라지면
   * state 도 사라진다.
   */
  active: boolean;
  window: MotionWindow;
  transport: PlaybackTransport | null;
  onSelect: (seconds: number) => void;
};

/** 팔별 원본 그래프와 구간 판정을 한 행에 둔다. 팔 방향은 structure 메타데이터만 사용한다. */
export function GripperSegmentRows(props: Props) {
  const rows = useMemo(() => {
    const groups = new Map<
      string,
      { id: string; label: string; metric: string; names: string[]; side: string | null }
    >();
    // 행 목록은 **개요가 정한다**. 상세가 도착할 때 목록이 바뀌면 행이 리마운트된다. 그러면
    // 임계값 설정이 사라진다. 개요와 상세는 같은 (채널, 지표) 집합을 가진다.
    for (const { series } of props.source.values()) {
      if (!series.metric.startsWith("value.")) continue;
      const side = props.channels.get(series.channel)?.arm_side ?? null;
      const id = `${side ?? series.channel}:${series.metric}`;
      const previous = groups.get(id);
      if (previous) previous.names.push(series.channel);
      else
        groups.set(id, {
          id,
          metric: series.metric,
          side,
          names: [series.channel],
          label:
            side === "left"
              ? "Left 그리퍼"
              : side === "right"
                ? "Right 그리퍼"
                : channelTitle(series.channel, props.channels.get(series.channel)),
        });
    }
    // 한 행은 같은 그리퍼의 명령 값과 측정 값을 나란히 그린다.
    return [...groups.values()].sort(
      (a, b) =>
        (a.side === "left" ? 0 : a.side === "right" ? 1 : 2) -
        (b.side === "left" ? 0 : b.side === "right" ? 1 : 2),
    );
  }, [props.source, props.channels]);
  return (
    <section aria-label="그리퍼 Segments" className={styles.root}>
      {rows.length === 0 ? <EmptyState size="sm">표시할 그리퍼 채널이 없습니다.</EmptyState> : null}
      {rows.map((row) => (
        <GripperRow
          key={row.id}
          {...props}
          metric={row.metric}
          rowNames={row.names}
          label={row.label}
        />
      ))}
    </section>
  );
}

function GripperRow({
  source,
  channels,
  active,
  window,
  transport,
  onSelect,
  metric,
  rowNames,
  label,
}: Props & { metric: string; rowNames: readonly string[]; label: string }) {
  // 기본 판정 소스는 Observation 이다. 명령(Action)보다 실제로 측정한 값을 먼저 쓴다.
  const defaultSource =
    rowNames.find((name) => renderGroup(channels.get(name), name) === "observation") ??
    rowNames[0]!;
  const [applied, setApplied] = useState<GripperSettings | null>(null);
  const [settingsOpen, setSettingsOpen] = useState(false);
  // 그리퍼를 숨기면 설정 창도 닫는다. 적용한 임계값은 유지한다.
  useEffect(() => {
    if (!active) setSettingsOpen(false);
  }, [active]);
  const sourceChannel = applied?.source ?? defaultSource;
  const judged = source.get(motionSeriesKey(sourceChannel, metric));
  // 구간은 **상세 표본으로만** 판정한다. 개요는 상류가 줄인 표본이다. 개요에서는 짧은 개폐
  // 펄스가 사라지고 경계 시각이 어긋난다. 그래프는 개요를 먼저 그린다. 판정은 정밀 표본을 기다린다.
  const detailed = judged !== undefined && !judged.preview;
  // 색·파선은 채널 카드와 같은 고정표를 쓴다. 그래서 한 화면에서 Observation 표시가 한 가지다.
  const lines = useMemo(
    () =>
      !active
        ? []
        : rowNames.flatMap((name): MotionPlotLine[] => {
            const pick = source.get(motionSeriesKey(name, metric));
            return pick === undefined
              ? []
              : [
                  {
                    series: pick.series,
                    preview: pick.preview,
                    title: channelTitle(name, channels.get(name)),
                    ...MOTION_SERIES_STYLE[renderGroup(channels.get(name), name)],
                  },
                ];
          }),
    [active, source, metric, rowNames, channels],
  );
  const segments = useMemo(
    () =>
      applied && judged && !judged.preview
        ? gripperSegments(judged.series.points, applied.threshold, applied.openAbove)
        : [],
    [judged, applied],
  );
  const span = window.end - window.start;
  return (
    <div className={styles.channel} data-applied={applied !== null || undefined}>
      <MotionPlot
        lines={lines}
        window={window}
        transport={transport}
        onSelect={onSelect}
        label={label}
        compact
        headerAction={
          <HatTextTooltip
            text="임계값과 열림 방향을 설정하면 분석 구간을 표시합니다."
            placement="left"
          >
            <IconButton
              size="sm"
              variant="ghost"
              text={applied ? "구간 설정" : "임계값 설정"}
              // 양팔이면 같은 이름의 버튼이 둘 생긴다. 그래서 접근명에 팔을 넣어 구분한다.
              aria-label={`${label} ${applied ? "구간 설정" : "임계값 설정"}`}
              onClick={() => setSettingsOpen(true)}
            />
          </HatTextTooltip>
        }
      />
      {applied ? (
        <div>
          {detailed ? (
            <div className={styles.track} aria-label={`${label} 구간 타임라인`}>
              {span > 0
                ? segments.map((segment, index) => {
                    const start = Math.max(segment.start, window.start),
                      end = Math.min(segment.end, window.end);
                    if (end <= start) return null;
                    const caption = `${label} ${index + 1}. ${segment.open ? "열림" : "닫힘"} · ${segment.start.toFixed(2)}–${segment.end.toFixed(2)}초`;
                    return (
                      <button
                        type="button"
                        key={index}
                        data-open={segment.open}
                        title={caption}
                        aria-label={caption}
                        style={{
                          left: `${((start - window.start) / span) * 100}%`,
                          width: `${((end - start) / span) * 100}%`,
                        }}
                        onClick={() => onSelect(segment.start)}
                      />
                    );
                  })
                : null}
            </div>
          ) : (
            // 표본이 오면 같은 자리·같은 높이에 구간 막대가 그려진다. 그래서 행이 움직이지 않는다.
            <div className={styles.track} role="status">
              <span className={styles.trackWaiting}>
                <Spinner size="sm" className={inlineSpinnerClassName} />
                정밀 표본이 오면 구간을 표시합니다.
              </span>
            </div>
          )}
          <p>
            {applied.threshold} {applied.openAbove ? "이상" : "이하"} 열림 ·{" "}
            {renderGroup(channels.get(sourceChannel), sourceChannel) === "observation"
              ? "Observation"
              : "Action"}
            {detailed ? ` · ${segments.length}구간` : null}
          </p>
        </div>
      ) : null}
      {active && settingsOpen ? (
        <GripperSettingsDialog
          label={label}
          sources={rowNames}
          channels={channels}
          applied={applied}
          defaultSource={defaultSource}
          onClose={() => setSettingsOpen(false)}
          onApply={(settings) => {
            setApplied(settings);
            setSettingsOpen(false);
          }}
          onClear={
            applied
              ? () => {
                  setApplied(null);
                  setSettingsOpen(false);
                }
              : undefined
          }
        />
      ) : null}
    </div>
  );
}
